import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Hand } from "lucide-react";
import { Phone } from "./Phone";
import { Chapter } from "./Reveal";

const STAGES = [
  { key: "tap", label: "TAP", screen: "idle" },
  { key: "nfc", label: "NFC DETECTED", screen: "nfc" },
  { key: "open", label: "OPENING REVIEW PAGE", screen: "nfc" },
  { key: "review", label: "LEAVE A REVIEW", screen: "review" },
];

const PRODUCTS = [
  { key: "stand", label: "Stand", img: "/assets/demo-stand.webp", alt: "Revora NFC review stand" },
  { key: "plate", label: "Plate", img: "/assets/demo-card.webp", alt: "Revora NFC review plate" },
  { key: "card", label: "Handheld card", img: "/assets/demo-plate.webp", alt: "Revora handheld NFC review card" },
];

export const TapDemo = () => {
  const [stage, setStage] = useState(0);
  const [tapped, setTapped] = useState(false);
  const [product, setProduct] = useState(PRODUCTS[0]);

  const advance = () => {
    if (stage >= STAGES.length - 1) return;
    setTapped(true);
    setTimeout(() => setTapped(false), 900);
    setStage((s) => s + 1);
  };

  return (
    <section id="how-it-works" className="bg-ink text-white py-28 sm:py-36 relative noise overflow-hidden" data-testid="tap-demo-section">
      <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-volt/10 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 relative">
        <Chapter number="02" label="How It Works" dark />
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="rvr-title font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
              Try the tap.<br />
              <span className="text-white/40">Right here.</span>
            </h2>
            <p className="mt-6 text-white/55 max-w-md leading-relaxed">
              This is the exact experience your customer has. No app, no typing, no searching —
              a single tap opens your Google review page.
            </p>

            <div className="mt-10" data-testid="demo-product-selector">
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/35 mb-3">Try it with</p>
              <div className="flex flex-wrap gap-2">
                {PRODUCTS.map((p) => (
                  <button
                    key={p.key}
                    data-testid={`demo-product-${p.key}`}
                    onClick={() => setProduct(p)}
                    className={`px-4 py-2 min-h-[44px] rounded-full text-xs font-bold tracking-[0.1em] uppercase border transition-colors duration-300 ${
                      product.key === p.key ? "bg-volt text-ink border-volt" : "border-white/20 text-white/60 hover:border-white/50 hover:text-white"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-1" data-testid="demo-stages">
              {STAGES.map((s, i) => (
                <button
                  key={s.key}
                  data-testid={`demo-stage-${s.key}`}
                  onClick={() => setStage(i)}
                  className={`flex items-center gap-4 py-3.5 px-4 -mx-4 rounded-lg text-left transition-colors duration-300 ${
                    i === stage ? "bg-white/10" : "hover:bg-white/5"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full transition-colors duration-300 ${i <= stage ? "bg-volt" : "bg-white/20"}`} />
                  <span className={`text-sm font-bold tracking-[0.15em] ${i === stage ? "text-white" : "text-white/40"}`}>
                    {s.label}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-8 flex gap-3">
              <button
                data-testid="demo-tap-button"
                onClick={advance}
                disabled={stage >= STAGES.length - 1}
                className="inline-flex items-center gap-3 bg-volt text-ink font-bold px-7 py-3.5 rounded-full hover:bg-voltdark hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-300"
              >
                <Hand className="w-4 h-4" />
                {stage === 0 ? "Tap to try" : "Continue"}
              </button>
              <button
                data-testid="demo-reset-button"
                onClick={() => setStage(0)}
                className="inline-flex items-center gap-2 border border-white/20 text-white/70 font-bold px-6 py-3.5 rounded-full hover:border-white/50 hover:text-white transition-colors duration-300"
              >
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>
          </div>

          <div className="relative h-[520px] flex items-center justify-center" data-testid="demo-stage-visual">
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.img
                  key={product.key}
                  src={product.img}
                  alt={product.alt}
                  initial={{ opacity: 0, y: 16, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -12, scale: 0.97 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="w-auto h-[320px] sm:h-[380px] [filter:drop-shadow(0_24px_35px_rgba(0,0,0,0.55))]"
                  data-testid="demo-product-image"
                />
              </AnimatePresence>
              <AnimatePresence>
                {tapped && (
                  <>
                    {[0, 1, 2].map((r) => (
                      <motion.span
                        key={r}
                        initial={{ scale: 0.4, opacity: 0.9 }}
                        animate={{ scale: 1.9, opacity: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1, delay: r * 0.18, ease: "easeOut" }}
                        className="absolute inset-0 rounded-[4%] border-2 border-volt pointer-events-none"
                      />
                    ))}
                  </>
                )}
              </AnimatePresence>
            </div>
            <motion.div
              animate={stage >= 1 ? { x: -40, y: 30, rotate: -4 } : { x: 60, y: -10, rotate: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-2 sm:right-10 top-4"
            >
              <Phone screen={STAGES[stage].screen} businessName="Your Business" className="!w-[200px] sm:!w-[220px]" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
